import {styled, Tab, Tabs} from "@mui/material";
import {
    Column,
    lookupNestedProp,
    NestedKeyOf,
    RenderStringCol,
    TableSelectionContext,
    TID
} from "../../../misc/scroller/Table";
import {projectLabel} from "../../../api/Projects";
import React, {createContext, useContext, useEffect, useMemo, useState} from "react";
import {css} from "@emotion/css";
import {FilterContext} from "../../../misc/scroller/Filter";
import {CheckboxProvider} from "../../project/shopdrawing/Checkbox";
import { TabWrapper } from "../../project/shopdrawing/TabWrapper";
import {Hardware} from "./toSource/Hardware";
import {Door} from "./toSource/Door";
import {Frame} from "./toSource/Frame";
import {SubmitButton} from "./toSource/SubmitButton";
import {purchasingPopoutPanel} from "./PurchaseOrderList";
import {useSyncedRef} from "../../../misc/SyncedRef";
import {Div10Tabs, TDiv10Type} from "../../project/shopdrawing/div10/Div10Tabs";
import {Div10} from "./toSource/Div10";
import {FrameAnchors} from "./toSource/FrameAnchors";
import {InventoryModeToggle} from "./InventoryModeToggle";
import {Component} from "./toSource/Component";
import {Glazing} from "./toSource/Glazing";
import {DistributionMode, DistributionModeToggle} from "./DistributionModeToggle";
import {SelectFilterForCol} from "../../../misc/SelectFilterForCol";
import {Misc} from "./toSource/Misc";

export const toSourceTableName = "purchasing.to-source-items"

// Shared project filter for the to-source tabs. The tabs are separate tables shown
// one at a time, but the project filter is meant to be common across all of them:
// picking a project on one tab keeps it applied when switching tabs, and clearing
// it clears it everywhere. Held in memory (resets on page refresh). null when a
// ProjectCol is used outside this provider, in which case it filters per-table.
const ToSourceProjectFilterContext = createContext<{
    value: string;
    setValue: (value: string) => void;
} | null>(null);

function ToSourceProjectFilterProvider(props: { children: React.ReactNode }) {
    const [value, setValue] = useState("");
    const ctx = useMemo(() => ({value, setValue}), [value]);
    return (
        <ToSourceProjectFilterContext.Provider value={ctx}>
            {props.children}
        </ToSourceProjectFilterContext.Provider>
    );
}

export function ToSourcePopout() {

    const ctx = useContext(TableSelectionContext);
    const refreshTable = useSyncedRef(ctx.refresh);
    const [props, setProps] = useState<ToSourceProps|null>(null);

    useEffect(() => {
        purchasingPopoutPanel.sendToServer("ping")
        const sub = purchasingPopoutPanel.fromServer.subscribeAndFireLast(value => {
            if(value === "refresh-to-source") {
                refreshTable.current(toSourceTableName);
            } else {
                setProps(value);
            }
        })

        return () => sub.cancel();
    }, [refreshTable]);

    if(!props) {
        return (<PartsToSource isDraft={false} />)
    }

    return (<PartsToSource {...props} />);
}

export type ToSourceProps = {
    purchaseOrder?: number;
    isDraft: boolean;
    supplier?: {
        id: number;
        name: string;
    }
    defaultTab?: SourceTab;
}

// must match tabs below
export type SourceTab = "hardware"|"frame"|"door"|"div10"|"glazing"

const defaultDiv10Tab = "wr-accessory";

export function PartsToSource(props: ToSourceProps) {
    const [tab, setTab] = useState(props.defaultTab || "hardware")
    const [tab2, setTab2] = useState<TDiv10Type>(defaultDiv10Tab)
    const [sourceMode, setSourceMode] = useState<"to-order"|"stock">("to-order")
    const [distributionMode, setDistributionMode] = useState<DistributionMode>("focused")

    useEffect(() => {
        setTab(props.defaultTab || "hardware")
    }, [props.defaultTab]);

    return (
        <div style={{display: "flex", flexDirection: "column", height: "100%"}}>
            <SourceHeader>
                Parts to Source
            </SourceHeader>

            <CheckboxProvider>
              <ToSourceProjectFilterProvider>
                <div style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "auto",
                }}>
                    <TabWrapper>
                        <Tabs className={tabGroup} value={tab} onChange={(e, value) => {
                            setTab(value)
                            setTab2(defaultDiv10Tab)
                        }}>
                            <Tab label="Frames" value="frame" />
                            <Tab label="Doors" value="door" />
                            <Tab label="Hardware" value="hardware" />
                            <Tab label="Glazing" value="glazing" />
                            <Tab label="Div 10" value="div10" />
                            <Tab label="Anchors" value="frame-anchors" />
                            <Tab label="Components" value="component" />
                            <Tab label="Misc" value="misc" />
                            <div style={{flex: 1}} />
                        </Tabs>
                        <div className={rightGroup}>
                            <DistributionModeToggle value={distributionMode} onChange={setDistributionMode} />
                            <InventoryModeToggle value={sourceMode} onChange={setSourceMode} />
                            <SubmitButton purchaseOrder={props.purchaseOrder} isDraft={props.isDraft} sourceMode={sourceMode} />
                        </div>
                    </TabWrapper>
                    {tab === "div10" && tab2 && <Div10Tabs tab={tab2} onChange={setTab2} />}


                    <div style={{flex: 1, display: "flex", overflow: "hidden"}}>
                        {tab === "frame" ? <Frame {...props} sourceMode={sourceMode} distributionMode={distributionMode} /> :
                            tab === "door" ? <Door {...props} sourceMode={sourceMode} distributionMode={distributionMode} /> :
                                tab === "hardware" ? <Hardware {...props} sourceMode={sourceMode} distributionMode={distributionMode} /> :
                                    tab === "div10" ? <Div10 {...props} tab={tab2} sourceMode={sourceMode} distributionMode={distributionMode} /> :
                                    tab === "glazing" ? <Glazing {...props} sourceMode={sourceMode} distributionMode={distributionMode} /> :
                                    tab === "component" ? <Component {...props} sourceMode={sourceMode} distributionMode={distributionMode} /> :
                                        tab === "misc" ? <Misc tab={tab} sourceMode={sourceMode} {...props} /> :
                                        <FrameAnchors {...props} sourceMode={sourceMode} distributionMode={distributionMode} />
                        }
                    </div>
                </div>
              </ToSourceProjectFilterProvider>
            </CheckboxProvider>
        </div>

    )
}

export const tabGroup = css({
    minHeight: "0px !important",
    flex: 1,

    "& .MuiTabs-flexContainer": {
        height: "100%",
    },

    "& .MuiButtonBase-root": {
        paddingTop: 4,
        paddingBottom: 4,
        minHeight: 0,
        fontSize: "0.7rem",
    }
})

export const tabGroupBig = css({
    minHeight: "0px !important",
    flex: 1,

    "& .MuiTabs-flexContainer": {
        height: "100%",
    },

    "& button.MuiButtonBase-root": {
        paddingTop: 16,
        paddingBottom: 16,
        minHeight: 0,
        fontSize: "1.25rem",
    }
})

export const tabGroupMedium = css({
    minHeight: "0px !important",
    flex: 1,

    "& .MuiTabs-flexContainer": {
        height: "100%",
    },

    "& button.MuiButtonBase-root": {
        paddingTop: 8,
        paddingBottom: 8,
        minHeight: 0,
        fontSize: "1rem",
    }
})

const rightGroup = css({
    display: "flex",

    "& .MuiButtonBase-root": {
        paddingTop: 4,
        paddingBottom: 4,
        minHeight: 0,
        fontSize: "0.7rem",
    }
})

// renders "P1001 - Name"; the filter matches on the bare project name
export function ProjectCol<T extends TID>(name: string, nameCol: NestedKeyOf<T>, numberCol: NestedKeyOf<T>, width?: number) {
    let str = RenderStringCol<T>(name, row => projectLabel({
        projectNumber: lookupNestedProp(row, numberCol) as number | null,
        name: lookupNestedProp(row, nameCol) as string | null,
    }), width) as Column<T>;

    str.renderFilter = (props) => <ProjectSelector<T> nameCol={nameCol as any} numberCol={numberCol as any} {...props} />
    str.filter = (row: T, value: string) => {
        const val = lookupNestedProp(row, nameCol)
        return val && val.toString().trim().toLowerCase() === value.trim()
    }

    return str;
}

function ProjectSelector<T extends object>(props: {
    nameCol: NestedKeyOf<T>;
    numberCol: NestedKeyOf<T>;
    value: string;
    onChange(value: string): void;
}) {
    const {srcData, onClear} = useContext(FilterContext);
    const shared = useContext(ToSourceProjectFilterContext);
    const [list, setList] = useState<string[]>([]);
    const [labels, setLabels] = useState<{[name: string]: string}>({});
    const listRef = useSyncedRef(list);

    useEffect(() => {

        const sub = srcData.subscribeAndFireLast(v => {
            let list: string[] = [];
            let labels: {[name: string]: string} = {};

            (v as T[]).map(rw => {
                const value = lookupNestedProp(rw, props.nameCol) as string;
                if(list.indexOf(value) === -1) {
                    list.push(value);
                    labels[value] = projectLabel({
                        projectNumber: lookupNestedProp(rw, props.numberCol) as number | null,
                        name: value,
                    });
                }

                return null;
            });

            list.sort();

            if(JSON.stringify(list) !== JSON.stringify(listRef.current)) {
                setList(list);
                setLabels(labels);
            }
        })

        return () => sub.cancel();
    }, [listRef, props.nameCol, props.numberCol, srcData]);

    const display = (name: string) => labels[name] || name;

    // mirror the shared project value into this table's filter cell so its rows
    // actually filter, and route this table's "clear filter" back to the shared value
    const onChange = props.onChange;
    useEffect(() => {
        if(!shared) return;
        if(props.value !== shared.value) onChange(shared.value);
    }, [shared, props.value, onChange]);

    useEffect(() => {
        if(!shared) return;
        const sub = onClear.subscribe(() => shared.setValue(""));
        return () => sub.cancel();
    }, [shared, onClear]);

    if(shared) {
        return (
            <SelectFilterForCol value={shared.value} list={list} onChange={shared.setValue} display={display} />
        );
    }

    return (
        <SelectFilterForCol value={props.value} list={list} onChange={props.onChange} display={display} />
    );
}


export const SourceHeader = styled("div")(props => ({
    backgroundColor: props.theme.palette.primary.dark,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    color: "white",
    paddingLeft: 8,
    paddingTop: 4,
    paddingBottom: 4,
    fontSize: "0.8rem",
}))